import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DashboardState } from './dashboard.reducer';
import { OverviewCard } from '../models/dashboard.models';

/**
 * Dashboard Selectors
 */

// Feature selector
export const selectDashboardState = createFeatureSelector<DashboardState>('dashboard');

// Basic selectors
export const selectDashboardLoading = createSelector(
  selectDashboardState,
  (state) => state.loading
);

export const selectDashboardError = createSelector(
  selectDashboardState,
  (state) => state.error
);

export const selectDashboardLastUpdated = createSelector(
  selectDashboardState,
  (state) => state.lastUpdated
);

export const selectUsageStats = createSelector(
  selectDashboardState,
  (state) => state.usage
);

export const selectSubscription = createSelector(
  selectDashboardState,
  (state) => state.subscription
);

export const selectRecentDocuments = createSelector(
  selectDashboardState,
  (state) => state.recentDocuments
);

export const selectDocumentsTotal = createSelector(
  selectDashboardState,
  (state) => state.documentsTotal
);

export const selectAPIKeysCount = createSelector(
  selectDashboardState,
  (state) => state.apiKeysCount
);

// Computed selectors - Matching HTML template design
export const selectOverviewCards = createSelector(
  selectUsageStats,
  selectSubscription,
  selectRecentDocuments,
  selectDocumentsTotal,
  selectAPIKeysCount,
  (usage, subscription, documents, documentsTotal, apiKeysCount): OverviewCard[] => {
    const cards: OverviewCard[] = [];

    const documentsProcessed = usage?.monthly_documents_used ?? 0;
    const orgSubtitle = 'Organization usage (this billing period)';

    cards.push({
      title: 'Documents Processed',
      subtitle: orgSubtitle,
      value: documentsProcessed.toLocaleString(),
      icon: 'description',
      color: 'primary'
    });

    const completedDocs = documents.filter((doc) => doc.parsing_status === 'completed');
    const estimatedPages = completedDocs.length > 0
      ? completedDocs.length * 3
      : documentsProcessed * 3;
    cards.push({
      title: 'Total Pages',
      subtitle: orgSubtitle,
      value: estimatedPages.toLocaleString(),
      icon: 'pages',
      color: 'accent'
    });

    const completedCount = documents.filter((doc) => doc.parsing_status === 'completed').length;
    const failedCount = documents.filter((doc) => doc.parsing_status === 'failed').length;
    const parsedTotal = completedCount + failedCount;
    const successRate = parsedTotal > 0
      ? `${((completedCount / parsedTotal) * 100).toFixed(1)}%`
      : '—';

    cards.push({
      title: 'Success Rate',
      subtitle: orgSubtitle,
      value: successRate,
      icon: 'check_circle',
      color: 'success'
    });

    cards.push({
      title: 'Avg Processing Time',
      subtitle: orgSubtitle,
      value: '—',
      icon: 'speed',
      color: 'warn'
    });

    return cards;
  }
);

// Usage percentage selector
export const selectUsagePercentages = createSelector(
  selectUsageStats,
  (usage) => {
    if (!usage) {
      return {
        apiCalls: 0,
        documents: 0,
        storage: 0
      };
    }

    return {
      apiCalls: usage.api_calls_percentage,
      documents: usage.documents_percentage,
      storage: usage.storage_percentage
    };
  }
);

// Check if data needs refresh (older than 5 minutes)
export const selectNeedsRefresh = createSelector(
  selectDashboardLastUpdated,
  (lastUpdated) => {
    if (!lastUpdated) return true;
    
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return lastUpdated < fiveMinutesAgo;
  }
);

// Plan limits selector
export const selectPlanLimits = createSelector(
  selectUsageStats,
  (usage) => {
    if (!usage) return null;

    return {
      apiCalls: {
        used: usage.monthly_api_calls_used,
        limit: usage.monthly_api_calls_limit,
        remaining: usage.api_calls_remaining,
        percentage: usage.api_calls_percentage
      },
      documents: {
        used: usage.monthly_documents_used,
        limit: usage.monthly_documents_limit,
        remaining: usage.documents_remaining,
        percentage: usage.documents_percentage
      },
      storage: {
        used: usage.storage_gb_used,
        limit: usage.storage_gb_limit,
        percentage: usage.storage_percentage
      }
    };
  }
);

